export const namespaced = true

/*
* sessionCache är för data som sparas lokalt på enheten tillsvidare, skillnaden mot localCache är att denna är unik per inloggad session
*
* När en session avslutas sparas state på sessionen i localStorage, när en ny session påbörjas laddas state in från localStorage
*/

// OBS: viktigt att alla state keys har en motsvarande mutation funktion som är exakt namngiven efter state key (uppercase snake_case med prefix SET_)
const initialState = () => ({
  recentSearchResults: [], // Array med senaste sökresultaten i AppSearch
  calendarCategoryFilter: [], // Array med category IDs som är valda i kalenderns filter, används bara om localSetting.calendarPreserveCategoryFilter är true
  mapImageAspectRatio: 1, // Denna är till för att få rätt aspect ratio på kartbilden innan bilden helt laddats in, när bilden laddats in används bildens egna ratio
  activeDashboardId: 1, // ID för den dashboard som är aktiv för användaren
  calendarScrollXPosition: 0, // Horisontell scrollposition i kalendern
  calendarScrollYPosition: 0, // Vertikal scrollposition i kalendern
  mainMenuPageVisits: {} // Antal besök per huvudmenylänk och datum
})

export const state = initialState()

export const mutations = {
  RESET_STATE (state) {
    // Mutation to reset state to defaults
    const defaultState = initialState()
    Object.keys(defaultState).forEach(key => {
      state[key] = defaultState[key]
    })
  },
  SET_RECENT_SEARCH_RESULTS (state, val) {
    state.recentSearchResults = val
    localStorage.setItem('SESSION_CACHE_RECENT_SEARCH_RESULTS', JSON.stringify(val))
  },
  SET_CALENDAR_CATEGORY_FILTER (state, val) {
    state.calendarCategoryFilter = val
    localStorage.setItem('SESSION_CACHE_CALENDAR_CATEGORY_FILTER', JSON.stringify(val))
  },
  SET_MAP_IMAGE_ASPECT_RATIO (state, val) {
    state.mapImageAspectRatio = val
    localStorage.setItem('SESSION_CACHE_MAP_IMAGE_ASPECT_RATIO', val.toString())
  },
  SET_ACTIVE_DASHBOARD_ID (state, val) {
    state.activeDashboardId = val
    localStorage.setItem('SESSION_CACHE_ACTIVE_DASHBOARD_ID', val.toString())
  },
  SET_CALENDAR_SCROLL_X_POSITION (state, val) {
    state.calendarScrollXPosition = val
    localStorage.setItem('SESSION_CACHE_CALENDAR_SCROLL_X_POSITION', val.toString())
  },
  SET_CALENDAR_SCROLL_Y_POSITION (state, val) {
    state.calendarScrollYPosition = val
    localStorage.setItem('SESSION_CACHE_CALENDAR_SCROLL_Y_POSITION', val.toString())
  },
  SET_MAIN_MENU_PAGE_VISITS (state, val) {
    const mainMenuPageVisits = val && typeof val === 'object' && !Array.isArray(val) ? val : {}
    state.mainMenuPageVisits = mainMenuPageVisits
    localStorage.setItem('SESSION_CACHE_MAIN_MENU_PAGE_VISITS', JSON.stringify(mainMenuPageVisits))
  }
}

export const actions = {
  initialise ({ commit, dispatch }) {
    // Tar bort gamla localstorage key som inte används längre, kan tas bort efter ett tag
    localStorage.removeItem('RECENT_SEARCH_RESULTS')
    localStorage.removeItem('CACHE_CALENDAR_CATEGORY_FILTER')
    localStorage.removeItem('CACHE_MAP_IMAGE_ASPECT_RATIO')
    localStorage.removeItem('LOCAL_SETTING_ACTIVE_DASHBOARD_ID')
    localStorage.removeItem('CACHE_CALENDAR_SCROLL_X_POSITION')
    localStorage.removeItem('CACHE_CALENDAR_SCROLL_Y_POSITION')
    // Raderna ovan kan tas bort efter ett tag

    let val = null

    val = localStorage.getItem('SESSION_CACHE_RECENT_SEARCH_RESULTS')
    val == null ? val = [] : val = JSON.parse(val) // [] default
    commit('SET_RECENT_SEARCH_RESULTS', val)

    val = localStorage.getItem('SESSION_CACHE_CALENDAR_CATEGORY_FILTER')
    val == null ? val = [] : val = JSON.parse(val) // [] default
    commit('SET_CALENDAR_CATEGORY_FILTER', val)

    val = localStorage.getItem('SESSION_CACHE_MAP_IMAGE_ASPECT_RATIO')
    val == null ? val = 1 : val = parseFloat(val) // 1 default
    commit('SET_MAP_IMAGE_ASPECT_RATIO', val)

    val = localStorage.getItem('SESSION_CACHE_ACTIVE_DASHBOARD_ID')
    val == null ? val = 1 : val = parseInt(val, 10) // 1 default
    commit('SET_ACTIVE_DASHBOARD_ID', val)

    val = localStorage.getItem('SESSION_CACHE_CALENDAR_SCROLL_X_POSITION')
    val == null ? val = 0 : val = parseInt(val, 10) // 0 default
    commit('SET_CALENDAR_SCROLL_X_POSITION', val)

    val = localStorage.getItem('SESSION_CACHE_CALENDAR_SCROLL_Y_POSITION')
    val == null ? val = 0 : val = parseInt(val, 10) // 0 default
    commit('SET_CALENDAR_SCROLL_Y_POSITION', val)

    val = localStorage.getItem('SESSION_CACHE_MAIN_MENU_PAGE_VISITS')
    try {
      val == null ? val = {} : val = JSON.parse(val)
    } catch {
      val = {}
    }
    commit('SET_MAIN_MENU_PAGE_VISITS', val)
  },
  packupSessionCache ({ state }) {
    const jsonState = JSON.stringify(state)
    return { jsonState }
  },
  unpackSessionCache ({ state, commit }, { jsonState }) {
    if (!jsonState) {
      return false
    }

    const unpackedState = JSON.parse(jsonState)

    // Återställer state med default värde före de skrivs över med sparade värden, så att eventella nya state keys som inte finns i unpackedState sätts till default och inte är kvar från tidigare session
    commit('RESET_STATE')

    const unpackedStateKeys = Object.keys(unpackedState)
    const stateKeys = Object.keys(state)

    for (let i = 0; i < unpackedStateKeys.length; i++) {
      const unpackedStateKey = unpackedStateKeys[i]

      if (!stateKeys.includes(unpackedStateKey)) {
        // state no longer exists, skip
        continue
      }

      // Convert camelCase key to SET_SNAKE_CASE mutation name
      // "activeDashboardId" -> "active_Dashboard_Id" -> "ACTIVE_DASHBOARD_ID"
      const snakeCaseKey = unpackedStateKey.replace(/[A-Z]/g, letter => `_${letter}`).toUpperCase()
      const mutationName = `SET_${snakeCaseKey}`

      // Commita sparad value till state
      commit(mutationName, unpackedState[unpackedStateKey])
    }
  }
}
